import React, { useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { MapPin, Mail, Phone, Send, Loader2, Leaf, Clock, MessageCircle, Instagram, Facebook, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const T = {
  it: { eyebrow: 'Contatti', title: 'Parliamone', subtitle: 'Hai una domanda o una proposta? Il nostro team ti risponde al piu\u0301 presto.', name: 'Nome e cognome', email: 'Email', phone: 'Telefono (opzionale)', subject: 'Oggetto (opzionale)', message: 'Il tuo messaggio', send: 'Invia messaggio', sending: 'Invio...', sent: 'Messaggio inviato! Ti risponderemo presto.', info: 'Informazioni', hours: 'Lun\u2013Ven 9:00\u201318:00', addrDef: 'Sila, Calabria \u00b7 Italia', emailDef: 'info@silaexperience.it', phoneDef: '+39 000 000 0000', maps: 'Apri su Google Maps', follow: 'Seguici' },
  en: { eyebrow: 'Contact', title: "Let's talk", subtitle: 'Got a question or a proposal? Our team will get back to you soon.', name: 'Full name', email: 'Email', phone: 'Phone (optional)', subject: 'Subject (optional)', message: 'Your message', send: 'Send message', sending: 'Sending...', sent: "Message sent! We'll get back to you soon.", info: 'Information', hours: 'Mon\u2013Fri 9:00\u201318:00', addrDef: 'Sila, Calabria \u00b7 Italy', emailDef: 'info@silaexperience.it', phoneDef: '+39 000 000 0000', maps: 'Open in Google Maps', follow: 'Follow us' },
};

// Renderizza SOLO valori primitivi: evita "React error #31" (oggetti come figli React).
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

// Bordo input ad ALTO CONTRASTO (QA accessibilità): usa il token `foreground`
// con opacità, così resta leggibile su card chiara e adattivo in dark mode.
const FIELD_CLS = 'border-foreground/25 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30';

/**
 * Vista Contatti condivisa: UNICA fonte di verita' usata sia dalla pagina pubblica
 * (/contatti) sia dall'anteprima live del CRM admin, cosi' sono sempre speculari.
 * - preview: form non interattivo (nessun invio reale)
 * - embedded: spaziatura ridotta e nessuna animazione (per l'anteprima nel pannello)
 */
export function ContactsView({ info = {}, lang = 'it', preview = false, embedded = false }) {
  const t = T[lang === 'en' ? 'en' : 'it'];
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const address = str(info.address) || t.addrDef;
  const email = str(info.email) || t.emailDef;
  const phone = str(info.phone) || t.phoneDef;
  const whatsapp = str(info.whatsapp);
  const instagram = str(info.instagram);
  const facebook = str(info.facebook);
  const mapsUrl = str(info.maps_url);
  const intro = str(info.intro) || t.subtitle;

  const waHref = whatsapp ? `https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}` : null;
  const hasSocial = Boolean(instagram || facebook || mapsUrl);

  const submit = async (e) => {
    e.preventDefault();
    if (preview) return;
    setSending(true);
    try {
      const { data } = await api.post('/cms/contact-messages', form);
      toast.success(data?.message || t.sent);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      // formatApiError converte SEMPRE in stringa (gestisce anche gli array di errori
      // di validazione Pydantic {type,loc,msg,...} -> evita React error #31).
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  // Render STATICO (niente framer-motion): il contenuto e' sempre visibile
  // anche nel build di produzione minificato (evita form invisibile / opacity:0).
  const Wrap = ({ children, className }) => <div className={className}>{children}</div>;

  const heroPad = embedded ? 'pt-6 pb-4' : 'pt-28 pb-8';
  const bodyPad = embedded ? 'pb-6 gap-6' : 'pb-24 gap-8';

  return (
    <>
      <section className={`sila-section ${heroPad}`}>
        <Wrap>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]"><Leaf className="h-3.5 w-3.5" />{t.eyebrow}</span>
          <h1 className={`mt-4 font-display font-bold tracking-tight ${embedded ? 'text-3xl' : 'text-4xl sm:text-5xl'}`}>{t.title}</h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">{intro}</p>
        </Wrap>
      </section>

      <section className={`sila-section grid grid-cols-1 lg:grid-cols-5 ${bodyPad}`}>
        {/* Info */}
        <Wrap className="lg:col-span-2">
          <div className="grain-overlay h-full rounded-[28px] bg-primary p-8 text-primary-foreground shadow-[var(--shadow-soft)]">
            <h2 className="font-display text-2xl font-bold">{t.info}</h2>
            <ul className="mt-6 space-y-5 text-primary-foreground/90">
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0" /><span>{address}</span></li>
              <li className="flex items-start gap-3"><Mail className="mt-0.5 h-5 w-5 shrink-0" /><a href={`mailto:${email}`} className="hover:underline">{email}</a></li>
              <li className="flex items-start gap-3"><Phone className="mt-0.5 h-5 w-5 shrink-0" /><a href={`tel:${phone}`} className="hover:underline">{phone}</a></li>
              {waHref && (
                <li className="flex items-start gap-3"><MessageCircle className="mt-0.5 h-5 w-5 shrink-0" /><a href={waHref} target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="contact-whatsapp">WhatsApp: {whatsapp}</a></li>
              )}
              <li className="flex items-start gap-3"><Clock className="mt-0.5 h-5 w-5 shrink-0" /><span>{t.hours}</span></li>
            </ul>

            {hasSocial && (
              <div className="mt-6 border-t border-primary-foreground/15 pt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-foreground/70">{t.follow}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  {instagram && (
                    <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 transition-colors hover:bg-primary-foreground/20"><Instagram className="h-5 w-5" /></a>
                  )}
                  {facebook && (
                    <a href={facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 transition-colors hover:bg-primary-foreground/20"><Facebook className="h-5 w-5" /></a>
                  )}
                  {mapsUrl && (
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-maps" className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-4 py-2 text-sm font-medium transition-colors hover:bg-primary-foreground/20"><ExternalLink className="h-4 w-4" />{t.maps}</a>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8 h-40 overflow-hidden rounded-2xl">
              <img loading="lazy" src="https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=700" alt="Sila" className="h-full w-full object-cover" />
            </div>
          </div>
        </Wrap>

        {/* Form */}
        <Wrap className="lg:col-span-3" delay={0.1}>
          <form onSubmit={submit} className="rounded-[28px] border border-border bg-card p-6 shadow-[var(--shadow-sm)] sm:p-8" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="mb-1.5 block text-sm font-medium">{t.name}</label><Input className={FIELD_CLS} required disabled={preview} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
              <div><label className="mb-1.5 block text-sm font-medium">{t.email}</label><Input className={FIELD_CLS} required type="email" disabled={preview} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
              <div><label className="mb-1.5 block text-sm font-medium">{t.phone}</label><Input className={FIELD_CLS} disabled={preview} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></div>
              <div><label className="mb-1.5 block text-sm font-medium">{t.subject}</label><Input className={FIELD_CLS} disabled={preview} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" /></div>
            </div>
            <div className="mt-4"><label className="mb-1.5 block text-sm font-medium">{t.message}</label><Textarea className={FIELD_CLS} required disabled={preview} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></div>
            <Button type="submit" size="lg" disabled={sending || preview} className="mt-5 rounded-full" data-testid="contact-submit">{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}{sending ? t.sending : t.send}</Button>
          </form>
        </Wrap>
      </section>
    </>
  );
}

export default ContactsView;
